/// <reference types="../window" />
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { renderRoutes } from "react-router-config";
import { ThemeProvider } from "styled-components";
import { GoogleOAuthProvider } from '@react-oauth/google';

import routingStartsWith from "../../common/helpers/routingStartsWith";

import theme from "../style/theme";
import getUser from "../store/user/actions/getUser";
import ErrorBoundary from "../components/ErrorBoundary";
import routing from "../routing";
import { IStateTree } from "../store/interfaces";
import GlobalCss from "../style";

import Footer from "../components/Footer";
import Cookie from "../components/Cookie";
import Nav from "../components/Nav";
import ScrollToTop from "../components/ScrollToTop";
import { hasItem } from "../cookie";

import Notifications from "./Notifications";
import NewVersionAvailable from "./NewVersionAvailable";
import * as Sentry from "@sentry/browser";
import { IProps } from "./interfaces";
import { darkColors } from "../style/colors";
import { getDarkMode, saveDarkMode } from "./store";
import { googleClientId } from "../../common/parameters";

const App = ({ route }: IProps) => {
    const [loaded, setLoaded] = useState(false);
    const user = useSelector((state: IStateTree) => state.user);
    const [darkMode, setDarkMode] = useState(getDarkMode());
    const history = useHistory();
    const dispatch = useDispatch();
    const showCookie = !hasItem("cookie-accepted");
    const handleSetDarkMode = () => {
        saveDarkMode(!darkMode);
        setDarkMode(old => !old);
    }
    useEffect(() => {
        if (!user || !user.loggedIn) {
            if (process.env.NODE_ENV === "development") {
                dispatch(getUser());
            }
        } else {
            const { _id: id, email } = user;
            Sentry.configureScope(scope => {
                scope.setUser({
                    id,
                    email,
                });
            });
            if (window.OneSignal) {
                window.OneSignal.push(function () {
                    window.OneSignal.showNativePrompt();
                });
            }
        }
        setLoaded(true);
    }, []);
    if (user === null) return null;
    let isPlaying = getIsPlaying(history.location.pathname, history.location.search);
    return (
        <GoogleOAuthProvider clientId={googleClientId}>
            <div>
                <ThemeProvider theme={{
                    ...theme,
                    mode: darkMode ? "dark" : "light",
                    c: {
                        ...theme.c,
                        ...(darkMode ? darkColors : {})
                    }
                }}
                >
                    <>
                        <GlobalCss />
                        <ErrorBoundary>
                            {!isPlaying && <Nav darkMode={darkMode} setDarkMode={handleSetDarkMode} />}
                            <ScrollToTop>{renderRoutes(route.routes)}</ScrollToTop>
                            {loaded && <NewVersionAvailable />}
                            {!isPlaying && <Footer />}
                            {!isPlaying && showCookie && <Cookie />}
                            <Notifications />
                        </ErrorBoundary>
                    </>
                </ThemeProvider>
            </div>
        </GoogleOAuthProvider>
    );
}

export default App;

const getIsPlaying = (pathname: string, search: string) => {
    if (
        routingStartsWith(pathname, routing.share + "/") ||
        routingStartsWith(pathname, routing.play + "/") ||
        routingStartsWith(pathname, routing.publicPlay + "/") ||
        (routingStartsWith(pathname, routing.join) && pathname !== `/${routing.closedClassroom}`) ||
        (!routingStartsWith(pathname, routing.classes) &&
            !routingStartsWith(pathname, routing.homeworkCompleted) &&
            routingStartsWith(pathname, routing.homework) &&
            pathname !== "/" + routing.homework &&
            pathname !== "/" + routing.homework + "/" &&
            pathname.slice(0, routing.homeworkResult.length + 1) !== "/" + routing.homeworkResult) ||
        routingStartsWith(pathname, routing.battleOfTheHuns) ||
        routingStartsWith(pathname, routing.christmasEdition) ||
        search.includes("nativeApp=1")
    ) {
        return true;
    }
    return false;
};
